// Eigenbau: eingeschränkter MDXEditor-Wrapper — kein offenes MDX, suppressHtml
import { lazy, Suspense, useCallback, useRef, useState } from "react";
import type { MDXEditorMethods } from "@mdxeditor/editor";
import { Workflow } from "lucide-react";
import { MERMAID_VORLAGE } from "@/lib/seiten-inhalt";

const uebersetzungen: Record<string, string> = {
  "toolbar.blockTypes.paragraph": "Absatz",
  "toolbar.blockTypes.quote": "Zitat",
  "toolbar.blockTypes.heading": "Überschrift {{level}}",
  "toolbar.blockTypeSelect.selectBlockTypeTooltip": "Absatzart wählen",
  "toolbar.blockTypeSelect.placeholder": "Absatzart",
  "toolbar.bold": "Fett",
  "toolbar.removeBold": "Fett entfernen",
  "toolbar.italic": "Kursiv",
  "toolbar.removeItalic": "Kursiv entfernen",
  "toolbar.strikethrough": "Durchstreichen",
  "toolbar.removeStrikethrough": "Durchstreichen entfernen",
  "toolbar.inlineCode": "Inline-Code",
  "toolbar.removeInlineCode": "Inline-Code entfernen",
  "toolbar.bulletedList": "Aufzählung",
  "toolbar.numberedList": "Nummerierte Liste",
  "toolbar.checkList": "Checkliste",
  "toolbar.link": "Link einfügen",
  "toolbar.table": "Tabelle einfügen",
  "toolbar.codeBlock": "Codeblock einfügen",
  "toolbar.thematicBreak": "Trennlinie einfügen",
  "toolbar.richText": "Bearbeiten",
  "toolbar.diffMode": "Änderungen",
  "toolbar.source": "Markdown",
  "toolbar.image": "Bild einfügen",
  "codeBlock.language": "Sprache",
  "codeBlock.selectLanguage": "Sprache wählen",
  "dialogControls.save": "Übernehmen",
  "dialogControls.cancel": "Abbrechen",
  "createLink.url": "Adresse",
  "createLink.text": "Linktext",
  "createLink.title": "Titel",
  "createLink.saveTooltip": "Link übernehmen",
  "createLink.cancelTooltip": "Abbrechen",
  "uploadImage.title": "Bild einfügen",
  "uploadImage.url": "Absolute HTTPS-Adresse",
  "uploadImage.alt": "Alternativtext",
};

function deutsch(
  key: string,
  standard: string,
  werte?: Record<string, unknown>,
): string {
  let text = uebersetzungen[key] ?? standard;
  for (const [name, wert] of Object.entries(werte ?? {})) {
    text = text.replaceAll(`{{${name}}}`, String(wert));
  }
  return text;
}

const LazyMdx = lazy(async () => {
  const m = await import("@mdxeditor/editor");
  // CSS nur auf der Seitenroute
  await import("@mdxeditor/editor/style.css");

  return {
    default: function MdxEditorInner({
      markdown,
      onChange,
      lesend,
    }: {
      markdown: string;
      onChange: (wert: string) => void;
      lesend?: boolean;
    }) {
      const editor = useRef<MDXEditorMethods>(null);
      const [parserFehler, setzeParserFehler] = useState<string | null>(null);

      const mermaidEinfuegen = () => {
        editor.current?.focus(
          () => editor.current?.insertMarkdown(MERMAID_VORLAGE),
          { defaultSelection: "rootEnd" },
        );
      };

      return (
        <>
          <m.MDXEditor
            ref={editor}
            markdown={markdown}
            onChange={(wert, initialMarkdownNormalize) => {
              setzeParserFehler(null);
              if (!initialMarkdownNormalize) onChange(wert);
            }}
            readOnly={lesend}
            suppressHtmlProcessing
            trim={false}
            className="cubic-seiteneditor mdxeditor-full-height"
            contentEditableClassName="seiten-inhalt cubic-seiteneditor-inhalt"
            placeholder="Beginne mit dem Inhalt dieser Seite …"
            translation={deutsch}
            onError={({ error }) => {
              setzeParserFehler(error);
              console.error("[mdxeditor]", error);
            }}
            plugins={[
              m.headingsPlugin(),
              m.listsPlugin(),
              m.quotePlugin(),
              m.thematicBreakPlugin(),
              m.linkPlugin({
                validateUrl: (url) =>
                  /^https?:\/\//i.test(url.trim()) ||
                  url.trim().startsWith("#"),
              }),
              m.linkDialogPlugin({ showLinkTitleField: true }),
              m.imagePlugin({
                allowSetImageDimensions: true,
                imagePreviewHandler: async (src) => {
                  const adresse = src.trim();
                  if (!/^https:\/\//i.test(adresse)) {
                    throw new Error(
                      "Bilder benötigen eine absolute HTTPS-Adresse.",
                    );
                  }
                  return adresse;
                },
              }),
              m.tablePlugin(),
              m.markdownShortcutPlugin(),
              m.codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
              m.codeMirrorPlugin({
                codeBlockLanguages: {
                  txt: "Text",
                  mermaid: "Mermaid",
                  js: "JavaScript",
                  ts: "TypeScript",
                  tsx: "TSX",
                  css: "CSS",
                  json: "JSON",
                  bash: "Bash",
                  markdown: "Markdown",
                },
              }),
              m.diffSourcePlugin({
                viewMode: "rich-text",
                diffMarkdown: markdown,
              }),
              m.toolbarPlugin({
                toolbarContents: () => (
                  <m.DiffSourceToggleWrapper>
                    <m.ConditionalContents
                      options={[
                        {
                          when: (aktiverEditor) =>
                            aktiverEditor?.editorType === "codeblock",
                          contents: () => (
                            <>
                              <m.UndoRedo />
                              <m.Separator />
                              <m.ChangeCodeMirrorLanguage />
                            </>
                          ),
                        },
                        {
                          fallback: () => (
                            <>
                              <m.UndoRedo />
                              <m.Separator />
                              <m.BlockTypeSelect />
                              <m.BoldItalicUnderlineToggles
                                options={["Bold", "Italic"]}
                              />
                              <m.CodeToggle />
                              <m.StrikeThroughSupSubToggles
                                options={["Strikethrough"]}
                              />
                              <m.Separator />
                              <m.ListsToggle />
                              <m.CreateLink />
                              <m.InsertImage />
                              <m.InsertTable />
                              <m.InsertThematicBreak />
                              <m.InsertCodeBlock />
                              <m.ButtonWithTooltip
                                title="Mermaid-Diagramm einfügen"
                                aria-label="Mermaid-Diagramm einfügen"
                                onClick={mermaidEinfuegen}
                              >
                                <Workflow size={16} aria-hidden="true" />
                              </m.ButtonWithTooltip>
                            </>
                          ),
                        },
                      ]}
                    />
                  </m.DiffSourceToggleWrapper>
                ),
              }),
            ]}
          />
          {parserFehler ? (
            <div
              className="border-t border-warn/30 bg-wisch px-5 py-3"
              role="alert"
            >
              <p className="font-mono text-[11.5px] text-warn">
                Markdown konnte nicht vollständig dargestellt werden:{" "}
                {parserFehler}
              </p>
            </div>
          ) : null}
        </>
      );
    },
  };
});

type Props = {
  seiteId: string;
  initialMarkdown: string;
  onMarkdownChange: (markdown: string) => void;
  lesend?: boolean;
};

/**
 * Editor mit unkontrolliertem Inhalt nach Mount.
 * Die monotone lokale Revision gehört dem Elternteil und überlebt Moduswechsel.
 */
export function SeitenEditor({
  seiteId,
  initialMarkdown,
  onMarkdownChange,
  lesend,
}: Props) {
  const handleChange = useCallback(
    (wert: string) => onMarkdownChange(wert),
    [onMarkdownChange],
  );

  return (
    <div className="cubic-seiteneditor-rahmen min-h-[560px] border border-linie bg-papier">
      <Suspense
        fallback={
          <p
            className="p-5 font-mono text-[11.5px] text-tinte4"
            role="status"
          >
            Editor wird geladen …
          </p>
        }
      >
        <LazyMdx
          key={seiteId}
          markdown={initialMarkdown}
          onChange={handleChange}
          lesend={lesend}
        />
      </Suspense>
    </div>
  );
}
